import React from "react";
import {Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Call02Icon, CallEnd01Icon, ViewIcon} from "@hugeicons/core-free-icons";
import {useWebrtc} from "../../context/WebrtcContext";
import {isCallLive, useOpenCallConversation} from "./ActiveCallCard";
import {LiveAvatar} from "../../pages/registry-project/dashboard/components/conversationUi";

const TITLES = {ringing: "Incoming call", connecting: "Connecting…", active: "On call"};
// "Call customer" (see callCustomer in WebrtcProvider): the agent's leg is answered, then the customer is dialed
const PHASE_TITLES = {
  agent_ringing: "Connecting…", agent_answered: "Dialing…", customer_ringing: "Ringing…",
  customer_answered: "On call", ended: "Call ended", agent_failed: "Call failed",
};
const titleOf = (call) => (call.outbound && PHASE_TITLES[call.phase])
  || (call.group && call.state === "ringing" ? "Incoming group call" : TITLES[call.state]);

// The browser phone's call until it is live (see WebrtcProvider): ringing with Answer / Reject, then the connecting
// steps. Once the call is live its controls move to the phone panel (ActiveCallCard).
export const WebrtcCallPopup = () => {
  const {call, answer, reject, hangup} = useWebrtc();
  const openConversation = useOpenCallConversation(call);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const iconSize = isMobile ? 20 : 18;
  const actionSx = {
    flex: 1, height: isMobile ? 52 : 48, borderRadius: "16px", textTransform: "none", fontSize: 15, fontWeight: 600,
    transition: "background-color 0.2s ease, transform 0.2s ease", "&:active": {transform: "scale(0.97)"},
  };
  if (!call || isCallLive(call)) return null;

  return (
    <Paper elevation={0} role={"dialog"} aria-label={titleOf(call)} sx={{
      "@keyframes call-popup-in": {
        from: {opacity: 0, transform: isMobile ? "translate(-50%, -46%) scale(0.97)" : "translateY(-8px) scale(0.98)"},
        to: {opacity: 1, transform: isMobile ? "translate(-50%, -50%) scale(1)" : "translateY(0) scale(1)"},
      },
      animation: "call-popup-in 0.35s cubic-bezier(0.34, 1.3, 0.64, 1)",
      ...(isMobile ? {
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1400,
        width: "calc(100vw - 32px)", maxWidth: 420, maxHeight: "calc(100vh - 32px)", overflowY: "auto", p: 3, borderRadius: "26px", bgcolor: "#fff",
        boxShadow: "0 20px 60px rgba(35, 38, 77, 0.16)",
      } : {
        position: "fixed", top: 72, right: 16, zIndex: 1400, width: 352, maxWidth: "calc(100vw - 32px)", p: 2.5,
        borderRadius: "24px", bgcolor: "#fff", boxShadow: "0 20px 60px rgba(35, 38, 77, 0.16)",
      }),
    }}>
      <Stack direction={"row"} alignItems={"center"} gap={1.75}>
        <LiveAvatar size={52} iconSize={22}/>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8491F7", textTransform: "uppercase"}}>
            {titleOf(call)}
          </Typography>
          <Typography noWrap sx={{fontSize: 18, fontWeight: 700, color: "#23264D"}}>{call.caller}</Typography>
          <Typography noWrap sx={{fontSize: 13, color: "#8A8AA0"}}>
            {call.group ? `Group ${call.group.name || call.group.number || call.group.id} · ` : ""}
            {call.conversationId ? `Conversation #${call.conversationId}` : "Browser call"}
          </Typography>
        </Box>
      </Stack>

      {call.conversationId && call.projectId && (
        <Button
          onClick={openConversation} fullWidth startIcon={<HugeiconsIcon icon={ViewIcon} size={16}/>}
          sx={{
            mt: 2, height: isMobile ? 48 : 40, borderRadius: "14px", textTransform: "none", fontSize: 14, fontWeight: 600,
            color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.2)"},
            transition: "background-color 0.2s ease",
          }}
        >
          Open conversation
        </Button>
      )}

      <Stack direction={"row"} gap={1.5} sx={{mt: 2.5}}>
        {call.state === "ringing" ? (
          <>
            <Button onClick={reject} startIcon={<HugeiconsIcon icon={CallEnd01Icon} size={iconSize}/>} sx={{
              ...actionSx, color: "#C73A3A", bgcolor: "rgba(229, 72, 77, 0.12)", "&:hover": {bgcolor: "rgba(229, 72, 77, 0.2)"},
            }}>
              Reject
            </Button>
            <Button onClick={answer} startIcon={<HugeiconsIcon icon={Call02Icon} size={iconSize}/>} sx={{
              ...actionSx, color: "#fff", bgcolor: "#1E8A55", "&:hover": {bgcolor: "#187548"},
            }}>
              Answer
            </Button>
          </>
        ) : (
          <Button onClick={hangup} startIcon={<HugeiconsIcon icon={CallEnd01Icon} size={iconSize}/>} sx={{
            ...actionSx, color: "#C73A3A", bgcolor: "rgba(229, 72, 77, 0.12)", "&:hover": {bgcolor: "rgba(229, 72, 77, 0.2)"},
          }}>
            Hang up
          </Button>
        )}
      </Stack>
    </Paper>
  );
};
